import toLower from '@tinkoff/utils/string/toLower';
import eachObj from '@tinkoff/utils/object/each';
import toArray from '@tinkoff/utils/array/toArray';
import { LEVELS, LEVEL_NAMES } from './constants.browser.js';

const createRegexpFromNamespace = (namespace) => {
    return new RegExp(`^${namespace.replace(/\*/g, '.*?')}$`);
};
class Logger {
    // eslint-disable-next-line sort-class-members/sort-class-members
    constructor(options = { name: '' }) {
        this.name = toLower(options.name);
        this.key = options.key || this.name;
        if (Logger.instances[this.key]) {
            return Logger.instances[this.key];
        }
        this.beforeReporters = options.beforeReporters || [];
        this.reporters = options.reporters || [];
        this.filters = options.filters || [];
        this.extensions = options.extensions || [];
        this.defaults = options.defaults || {};
        if (options.level) {
            this.setLevel(LEVELS[options.level]);
        }
        if (options.enabled) {
            this.setLevel(LEVELS.trace);
        }
        this.checkEnabled();
        Logger.instances[this.key] = this;
        eachObj((level, levelName) => {
            this[levelName] = (...args) => {
                return this.log(level, args);
            };
        }, LEVELS);
    }
    static save() {
        var _a;
        (_a = Logger.onChange) === null || _a === void 0 ? void 0 : _a.call(Logger, {
            level: Logger.level,
            enabledName: this.enabledName,
            enabledLevel: this.enabledLevel,
        });
    }
    static load(state) {
        Logger.level = state.level;
        Logger.enabledName = state.enabledName;
        Logger.enabledLevel = state.enabledLevel;
    }
    static setLevel(newLevel) {
        var _a;
        Logger.level = (_a = LEVELS[newLevel]) !== null && _a !== void 0 ? _a : Logger.level;
        Logger.save();
    }
    static setOnChange(onChange) {
        Logger.onChange = onChange;
    }
    static enable(level, namespace) {
        const [lvl, ns] = namespace ? [LEVELS[level], namespace] : [LEVELS.trace, level];
        Logger.enabledName.push(ns);
        Logger.enabledLevel.push(lvl);
        Logger.save();
        const regexp = createRegexpFromNamespace(ns);
        eachObj((instance) => {
            const { name } = instance;
            if (regexp.test(name)) {
                instance.setLevel(lvl);
            }
        }, Logger.instances);
    }
    static disable(level, namespace) {
        const [lvl, ns] = namespace ? [LEVELS[level], namespace] : [LEVELS.silent, level];
        const index = Logger.enabledName.indexOf(ns);
        if (index > -1) {
            Logger.enabledName.splice(index, 1);
            Logger.enabledLevel.splice(index, 1);
        }
        else {
            Logger.enabledName.push(ns);
            Logger.enabledLevel.push(lvl);
        }
        Logger.save();
        const regexp = createRegexpFromNamespace(ns);
        eachObj((instance) => {
            const { name } = instance;
            if (regexp.test(name)) {
                instance.setLevel(lvl);
            }
        }, Logger.instances);
    }
    static clear() {
        Logger.level = LEVELS.silent;
        Logger.enabledName = [];
        Logger.enabledLevel = [];
        Logger.save();
        eachObj((instance) => {
            instance.setLevel(undefined);
        }, Logger.instances);
    }
    static setGlobalReporters(reporters) {
        const reportersArr = toArray(reporters);
        eachObj((instance) => {
            // eslint-disable-next-line no-param-reassign
            instance.reporters = reportersArr;
        }, Logger.instances);
    }
    child(options) {
        const opts = typeof options === 'string' ? { name: options } : options;
        const childKey = opts.key || opts.name;
        const name = this.name ? `${this.name}.${opts.name}` : opts.name;
        const key = this.key ? `${this.key}.${childKey}` : childKey;
        return new Logger({
            beforeReporters: this.beforeReporters,
            reporters: this.reporters,
            filters: this.filters,
            extensions: this.extensions,
            defaults: this.defaults,
            ...opts,
            name,
            key,
        });
    }
    addBeforeReporter(reporter) {
        this.beforeReporters = this.beforeReporters.concat(reporter);
    }
    setBeforeReporters(reporters) {
        this.beforeReporters = toArray(reporters);
    }
    addReporter(reporter) {
        this.reporters = this.reporters.concat(reporter);
    }
    setReporters(reporters) {
        this.reporters = toArray(reporters);
    }
    addFilter(filter) {
        this.filters = this.filters.concat(filter);
    }
    setFilters(filters) {
        this.filters = toArray(filters);
    }
    addExtension(extension) {
        this.extensions = this.extensions.concat(extension);
        this.extensions.push(extension);
    }
    setExtensions(extensions) {
        this.extensions = toArray(extensions);
    }
    setLevel(level) {
        this.level = level;
    }
    checkEnabled() {
        const len = Logger.enabledName.length;
        for (let i = len - 1; i >= 0; i--) {
            const namespace = Logger.enabledName[i];
            const regexp = createRegexpFromNamespace(namespace);
            if (regexp.test(this.name)) {
                this.setLevel(Logger.enabledLevel[i]);
                return;
            }
        }
    }
    createLogObj(level, args) {
        return {
            date: new Date(),
            ...this.defaults,
            name: this.name,
            type: LEVEL_NAMES[level],
            level,
            args,
        };
    }
    log(level, args) {
        let logObj;
        if (this.beforeReporters.length) {
            logObj = this.createLogObj(level, args);
        }
        for (const reporter of this.beforeReporters) {
            reporter.log(logObj);
        }
        if (typeof this.level === 'undefined') {
            if (level > Logger.level) {
                return false;
            }
        }
        else if (level > this.level) {
            return false;
        }
        if (!logObj) {
            logObj = this.createLogObj(level, args);
        }
        for (const filter of this.filters) {
            if (filter.filter(logObj) === false) {
                return false;
            }
        }
        for (const extension of this.extensions) {
            logObj = extension.extend(logObj);
        }
        for (const reporter of this.reporters) {
            reporter.log(logObj);
        }
        return true;
    }
}
Logger.level = LEVELS.silent;
Logger.instances = {};
Logger.enabledName = [];
Logger.enabledLevel = [];

export { Logger };
